import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowLeft,
  BarChart3,
  CreditCard,
  Map as MapIcon,
  Receipt,
  TrendingUp,
  Trophy,
  Users
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { BrazilMap } from './components/dashboard/BrazilMap';
import { KpiCard } from './components/dashboard/KpiCard';
import {
  ANOS,
  EMPRESAS,
  filtrar,
  formatBRL,
  formatCompact,
  formatNumber,
  REGIOES_BRASIL,
  serieMensal,
  somarPor
} from './lib/dashboard-data';
import { cn } from './lib/utils';

const CORES = [
  "#3b82f6", // Azul
  "#10b981", // Verde
  "#f59e0b", // Laranja/Amarelo
  "#8b5cf6", // Roxo
  "#ec4899", // Rosa
  "#06b6d4"  // Ciano
];

const REGIOES = ["TODAS", "Norte", "Nordeste", "Centro-Oeste", "Sudeste", "Sul"];

const tooltipStyle = {
  backgroundColor: "#090d16",
  border: "1px solid #1e293b",
  borderRadius: 8,
  fontSize: 12,
  color: "#f8fafc"
};

const base_url = 'https://onboard.fck.ind.br';

function numero(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string') return Number(value) || 0;

  const texto = value.trim();
  if (!texto) return 0;
  const normalizado = texto.includes(',')
    ? texto.replace(/\./g, '').replace(',', '.')
    : texto;
  return Number(normalizado) || 0;
}

function normalizarRegistro(item, empresaSelecionada) {
  const uf = String(item?.uf ?? item?.UF ?? item?.estado ?? item?.ESTADO ?? '').trim().toUpperCase();

  return {
    ano: numero(item?.ano ?? item?.ANO),
    mes: numero(item?.mes ?? item?.mês ?? item?.MES ?? item?.MÊS),
    empresa: String(item?.empresa ?? item?.EMPRESA ?? empresaSelecionada),
    uf,
    cidade: String(item?.cidade ?? item?.CIDADE ?? 'Não informada').trim() || 'Não informada',
    regiao: String(item?.regiao ?? item?.REGIAO ?? REGIOES_BRASIL[uf] ?? 'Não informada').trim() || 'Não informada',
    vendedor: String(item?.vendedor ?? item?.VENDEDOR ?? 'Não informado').trim() || 'Não informado',
    formaPagamento: String(item?.formaPagamento ?? item?.FORMA_PAGAMENTO ?? item?.forma_pagamento ?? 'Não informado').trim() || 'Não informado',
    valorTotal: numero(item?.valorTotal ?? item?.valor_Total ?? item?.VALORTOTAL ?? item?.valor_total),
    pedidos: numero(item?.pedidos ?? item?.PEDIDOS ?? item?.quantidadePedidos ?? item?.quantidade_pedidos)
  };
}

function extrairRegistros(resultado) {
  if (Array.isArray(resultado)) return resultado;

  const registros = resultado?.registros
    ?? resultado?.Registros
    ?? resultado?.data
    ?? resultado?.dados
    ?? resultado?.listaFaturamento;

  return Array.isArray(registros) ? registros : [];
}

function Panel({ title, subtitle, action, className, bodyClassName, children }) {
  return (
    <div className={cn("flex flex-col rounded-xl border border-slate-800 bg-slate-950/80 p-4 backdrop-blur-md shadow-sm", className)}>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-xs font-bold tracking-tight text-slate-100 uppercase">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-400">{subtitle}</p>}
        </div>
        {action && <div>{action}</div>}
      </div>
      <div className={cn("w-full flex-1 min-h-0", bodyClassName)}>
        {children}
      </div>
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <label className="flex flex-col gap-1 w-full">
      <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

export default function DashboardPage({ onClose }) {
  const [ano, setAno] = useState(2026);
  const [empresa, setEmpresa] = useState("TODAS");
  const [regiao, setRegiao] = useState("TODAS");

  const [dataset, setDataset] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    async function buscarDados() {
      const token = localStorage.getItem('@user_token');
      if (!token) {
        setError('Sessão expirada. Faça login novamente.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          anoInicio: String(Math.min(...ANOS)),
          anoFinal: String(Math.max(...ANOS)),
          empresa
        });
        const res = await fetch(`${base_url}/api/dashboard/Faturamento/graficogeral?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          signal: controller.signal
        });

        if (!res.ok) {
          if (res.status === 401) throw new Error('Não autorizado. Token inválido.');
          throw new Error('Não foi possível carregar os dados de faturamento.');
        }

        const resultado = await res.json();
        const registros = extrairRegistros(resultado);
        setDataset(registros.map((item) => normalizarRegistro(item, empresa)));
      } catch (err) {
        if (err.name !== 'AbortError') {
          setDataset([]);
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    buscarDados();
    return () => controller.abort();
  }, [empresa]);

  const linhas = useMemo(
    () => filtrar(dataset, { ano, empresa, regiao }) || [],
    [dataset, ano, empresa, regiao]
  );

  const linhasAnoAnterior = useMemo(
    () => filtrar(dataset, { ano: ano - 1, empresa, regiao }) || [],
    [dataset, ano, empresa, regiao]
  );

  const total = useMemo(
    () => linhas.reduce((s, r) => s + (r.valorTotal || 0), 0),
    [linhas]
  );

  const totalAnterior = useMemo(
    () => linhasAnoAnterior.reduce((s, r) => s + (r.valorTotal || 0), 0),
    [linhasAnoAnterior]
  );

  const pedidos = useMemo(
    () => linhas.reduce((s, r) => s + (r.pedidos || 0), 0),
    [linhas]
  );

  const variacao = totalAnterior > 0 ? ((total - totalAnterior) / totalAnterior) * 100 : 0;
  const ticket = pedidos > 0 ? total / pedidos : 0;

  const porUf = useMemo(() => {
    const agrupado = somarPor(linhas, "uf") || [];
    return agrupado.map((d) => ({
      uf: d.name,
      valor: d.valor || 0,
      pedidos: d.pedidos || 0
    }));
  }, [linhas]);

  const cidades = useMemo(
    () => (somarPor(linhas, "cidade") || []).slice(0, 8),
    [linhas]
  );

  const vendedores = useMemo(
    () => somarPor(linhas, "vendedor") || [],
    [linhas]
  );

  const pagamentos = useMemo(
    () => somarPor(linhas, "formaPagamento") || [],
    [linhas]
  );

  const regioes = useMemo(
    () => somarPor(linhas, "regiao") || [],
    [linhas]
  );

  const serie = useMemo(() => {
    const atual = serieMensal(linhas) || [];
    const anterior = serieMensal(linhasAnoAnterior) || [];
    return atual.map((m, i) => ({
      mes: m.mes,
      atual: m.valor || 0,
      anterior: anterior[i]?.valor || 0,
      pedidos: m.pedidos || 0
    }));
  }, [linhas, linhasAnoAnterior]);

  const acumulado = useMemo(() => {
    let soma = 0;
    return serie.map((m) => {
      soma += m.atual;
      return { mes: m.mes, acumulado: soma };
    });
  }, [serie]);

  const radarRegioes = useMemo(() => {
    const max = Math.max(1, ...regioes.map((r) => r.valor || 0));
    return REGIOES.filter((r) => r !== "TODAS").map((nome) => {
      const reg = regioes.find((r) => r.name === nome);
      return {
        regiao: nome,
        indice: Math.round(((reg?.valor || 0) / max) * 100)
      };
    });
  }, [regioes]);

  const rankingUf = useMemo(
    () =>
      porUf.slice(0, 10).map((d, i) => ({
        ...d,
        posicao: i + 1,
        share: total > 0 ? (d.valor / total) * 100 : 0,
        ticket: d.pedidos > 0 ? d.valor / d.pedidos : 0
      })),
    [porUf, total]
  );

  const topUf = porUf[0];

  const melhorMes = useMemo(() => {
    if (!serie.length) return null;
    return serie.reduce((a, b) => (b.atual > a.atual ? b : a), serie[0]);
  }, [serie]);

  return (
    <div className="min-h-screen w-full bg-[#030712] text-slate-100 font-sans p-4 lg:p-6">
      {/* Header Superior */}
      <header className="mb-6 flex flex-col gap-3 border-b border-slate-800 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-2 text-blue-500">
          <Activity size={16} />
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.28em]">
            Business Intelligence
          </span>
        </div>
        <h1 className="text-xl font-extrabold tracking-tight text-white lg:text-2xl">
          Painel de Faturamento Brasil
        </h1>
        <p className="text-xs text-slate-400">
          Cobertura nacional por unidade federativa, com evolução mensal, ranking comercial e mix de recebimentos.
        </p>
        {onClose && <button type="button" onClick={onClose} className="inline-flex items-center gap-2 self-start rounded-md border border-slate-700 px-3 py-2 text-xs font-bold text-slate-300 transition-colors hover:border-blue-500 hover:text-white">
          <ArrowLeft size={15} /> Voltar ao resumo
        </button>}
      </header>

      {loading && <div className="mb-4 rounded-md border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-300">Carregando dados de faturamento...</div>}
      {error && <div className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

      {/* Grid Principal com Sidebar Lateral */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Coluna Lateral Esquerda: Filtros e KPIs */}
        <aside className="flex flex-col gap-4 lg:col-span-3">
          {/* Seção de Filtros */}
          <div className="flex flex-col gap-3 rounded-xl border border-slate-800 bg-slate-950/80 p-4">
            <Campo label="Exercício">
              <div className="grid grid-cols-4 gap-1 rounded-md border border-slate-800 bg-slate-900 p-1">
                {ANOS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setAno(a)}
                    className={cn(
                      "rounded py-1 font-mono text-[11px] font-bold transition-colors",
                      ano === a
                        ? "bg-blue-600 text-white"
                        : "text-slate-400 hover:text-white"
                    )}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </Campo>

            <Campo label="Unidade">
              <select
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-800 bg-slate-900 px-2 text-xs text-slate-200 outline-none focus:border-blue-500"
              >
                {EMPRESAS.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
            </Campo>

            <Campo label="Macrorregião">
              <select
                value={regiao}
                onChange={(e) => setRegiao(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-800 bg-slate-900 px-2 text-xs text-slate-200 outline-none focus:border-blue-500"
              >
                {REGIOES.map((r) => (
                  <option key={r} value={r}>
                    {r === "TODAS" ? "Brasil inteiro" : r}
                  </option>
                ))}
              </select>
            </Campo>
          </div>

          {/* Cards de KPI Empilhados */}
          <div className="flex flex-col gap-3">
            <KpiCard
              label="Faturamento total"
              value={formatCompact(total)}
              hint={formatBRL(total)}
              variacao={variacao}
              icon={TrendingUp}
              accent="chart2"
            />
            <KpiCard
              label="Pedidos faturados"
              value={formatNumber(pedidos)}
              hint={`Melhor mês: ${melhorMes?.mes ?? "-"} · ${formatCompact(melhorMes?.atual ?? 0)}`}
              icon={Receipt}
              accent="primary"
            />
            <KpiCard
              label="Ticket médio"
              value={formatBRL(ticket)}
              hint={`${porUf.length} estados com faturamento`}
              icon={BarChart3}
              accent="chart3"
            />
            <KpiCard
              label="Estado líder"
              value={topUf?.uf ?? "-"}
              hint={`${formatCompact(topUf?.valor ?? 0)} · ${
                total > 0 ? (((topUf?.valor ?? 0) / total) * 100).toFixed(1) : "0"
              }% do total`}
              icon={Trophy}
              accent="chart1"
            />
          </div>
        </aside>

        {/* Área Principal de Gráficos (Centro + Direita) */}
        <main className="flex flex-col gap-4 lg:col-span-9">
          {/* Seção Superior: Mapa (Esquerda) e Evolução/Cidades (Direita) */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            {/* Mapa do Brasil */}
            <Panel
              title="Faturamento por estado"
              subtitle="Mapa nacional — intensidade e volume por UF"
              className="xl:col-span-5 min-h-[500px]"
              bodyClassName="flex items-center justify-center min-h-[420px]"
              action={<MapIcon className="text-slate-500" size={16} />}
            >
              <BrazilMap dados={porUf} />
            </Panel>

            {/* Evolução Mensal + Top Cidades e Radar */}
            <div className="flex flex-col gap-4 xl:col-span-7">
              <Panel
                title="Evolução mensal x ano anterior"
                subtitle={`${ano} comparado com ${ano - 1}`}
                className="h-[250px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={serie} margin={{ left: -20, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="gradAtual" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis dataKey="mes" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={formatCompact} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [formatBRL(v), name === "atual" ? String(ano) : String(ano - 1)]} />
                    <Area type="monotone" dataKey="atual" stroke="#3b82f6" strokeWidth={2} fill="url(#gradAtual)" />
                    <Line type="monotone" dataKey="anterior" stroke="#64748b" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </Panel>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Panel title="Top cidades" subtitle="8 maiores praças do período" className="h-[235px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cidades} layout="vertical" margin={{ left: -20, right: 12 }}>
                      <XAxis type="number" hide />
                      <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} width={100} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatBRL(v), "Faturamento"]} />
                      <Bar dataKey="valor" radius={[0, 4, 4, 0]} barSize={10}>
                        {cidades.map((_, i) => (
                          <Cell key={i} fill={CORES[i % CORES.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>

                <Panel title="Índice por macrorregião" subtitle="100 = região líder" className="h-[235px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarRegioes} outerRadius="65%">
                      <PolarGrid stroke="#1e293b" />
                      <PolarAngleAxis dataKey="regiao" tick={{ fill: "#94a3b8", fontSize: 9 }} />
                      <Radar dataKey="indice" stroke="#10b981" fill="#10b981" fillOpacity={0.35} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}`, "Índice"]} />
                    </RadarChart>
                  </ResponsiveContainer>
                </Panel>
              </div>
            </div>
          </div>

          {/* Seção Inferior: Vendedores, Pagamento e Ranking */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            <Panel title="Vendas por vendedor" subtitle="Ranking de performance individual" className="xl:col-span-5 h-[300px]" action={<Users className="text-slate-500" size={16} />}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={vendedores} margin={{ left: -20, right: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} interval={0} tickFormatter={(v) => (v ? v.split(" ")[0] : "")} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={formatCompact} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatBRL(v), "Total"]} />
                  <Bar dataKey="valor" fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={22} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Mix de formas de pagamento" subtitle="Participação no faturamento" className="xl:col-span-3 h-[300px]" action={<CreditCard className="text-slate-500" size={16} />}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pagamentos} dataKey="valor" nameKey="name" cx="50%" cy="40%" innerRadius={40} outerRadius={65} paddingAngle={3}>
                    {pagamentos.map((_, i) => (
                      <Cell key={i} fill={CORES[i % CORES.length]} stroke="#030712" />
                    ))}
                  </Pie>
                  <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 9, color: "#94a3b8" }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatBRL(v)} />
                </PieChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Ranking de estados" subtitle="Volume, participação e ticket médio" className="xl:col-span-4 h-[300px]" bodyClassName="overflow-y-auto">
              <table className="w-full text-left">
                <thead className="sticky top-0 bg-slate-950 z-10">
                  <tr className="text-[9px] uppercase tracking-wider text-slate-400 border-b border-slate-800">
                    <th className="pb-2 font-bold">#</th>
                    <th className="pb-2 font-bold">UF</th>
                    <th className="pb-2 text-right font-bold">Valor</th>
                    <th className="pb-2 text-right font-bold">Share</th>
                    <th className="pb-2 text-right font-bold">Ticket</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-[11px]">
                  {rankingUf.map((r) => (
                    <tr key={r.uf} className="border-t border-slate-800/60 hover:bg-slate-900/50 transition-colors">
                      <td className="py-1.5 text-slate-500">{r.posicao}</td>
                      <td className="py-1.5 font-bold text-slate-200">{r.uf}</td>
                      <td className="py-1.5 text-right font-semibold text-emerald-400">{formatCompact(r.valor)}</td>
                      <td className="py-1.5 text-right text-slate-400">{r.share.toFixed(1)}%</td>
                      <td className="py-1.5 text-right text-slate-400">{formatCompact(r.ticket)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>

          {/* Faturamento Acumulado */}
          <Panel title="Faturamento acumulado no exercício" subtitle="Curva de realização mês a mês" className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={acumulado} margin={{ left: -20, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="gradAcum" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ec4899" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#ec4899" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="mes" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={formatCompact} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatBRL(v), "Acumulado"]} />
                <Area type="monotone" dataKey="acumulado" stroke="#ec4899" strokeWidth={2} fill="url(#gradAcum)" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        </main>
      </div>

      <footer className="mt-8 flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 pt-4 font-mono text-[9px] uppercase tracking-wider text-slate-500">
        <span>Cobertura nacional · 27 unidades federativas processadas</span>
        <span>Atualizado a cada sincronização de faturamento</span>
      </footer>
    </div>
  );
}
